// Service to handle app opening and messaging functionality

// Map of supported apps and their launch methods
const supportedApps = {
  whatsapp: {
    name: 'WhatsApp',
    openMethod: () => window.open('https://web.whatsapp.com/', '_blank'),
    type: 'social'
  },
  facebook: {
    name: 'Facebook',
    openMethod: () => window.open('https://www.facebook.com/', '_blank'),
    type: 'social'
  },
  instagram: {
    name: 'Instagram',
    openMethod: () => window.open('https://www.instagram.com/', '_blank'),
    type: 'social'
  },
  twitter: {
    name: 'Twitter',
    openMethod: () => window.open('https://twitter.com/', '_blank'),
    type: 'social'
  },
  x: {
    name: 'X',
    openMethod: () => window.open('https://twitter.com/', '_blank'),
    type: 'social'
  },
  gmail: {
    name: 'Gmail',
    openMethod: () => window.open('https://mail.google.com/', '_blank'),
    type: 'email'
  },
  youtube: {
    name: 'YouTube',
    openMethod: () => window.open('https://www.youtube.com/', '_blank'),
    type: 'video'
  },
  google: {
    name: 'Google',
    openMethod: () => window.open('https://www.google.com/', '_blank'),
    type: 'search'
  },
};

// Search platforms
const searchPlatforms = {
  google: (query) => window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank'),
  youtube: (query) => window.open(`https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`, '_blank'),
  bing: (query) => window.open(`https://www.bing.com/search?q=${encodeURIComponent(query)}`, '_blank'),
};

// Open a specified app
export const openApp = (appName) => {
  const app = findApp(appName);
  
  if (app) {
    app.openMethod();
    return { success: true, message: `Opening ${app.name}` };
  } else {
    console.error(`App "${appName}" not supported`);
    return { 
      success: false, 
      message: `Sorry, "${appName}" is not supported. Supported apps are: ${Object.values(supportedApps).map(app => app.name).join(', ')}` 
    };
  }
};

// Get social media apps
export const getSocialMediaApps = () => {
  return Object.entries(supportedApps)
    .filter(([_, app]) => app.type === 'social')
    .map(([key, app]) => ({ key, name: app.name }));
};

// Get search platforms
export const getSearchPlatforms = () => {
  return Object.keys(searchPlatforms);
};

// Search on a specified platform
export const searchOnPlatform = (platform, query) => {
  if (searchPlatforms[platform.toLowerCase()]) {
    searchPlatforms[platform.toLowerCase()](query);
    return { success: true, message: `Searching for "${query}" on ${platform}` };
  } else {
    return { 
      success: false, 
      message: `Sorry, search on "${platform}" is not supported. Supported platforms are: ${Object.keys(searchPlatforms).join(', ')}` 
    };
  }
};

// Play a YouTube video
export const playYouTubeVideo = (query) => {
  // Due to browser security limitations, we can't automatically click 
  // on elements in a new window. Instead, we'll just open the search results.
  const searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
  window.open(searchUrl, '_blank');
  return { 
    success: true, 
    message: `Searching for "${query}" on YouTube. Click on a video to play it.` 
  };
};

// Find app by name (supports partial matches and aliases)
const findApp = (appName) => {
  appName = appName.toLowerCase().trim();
  
  // Direct match
  if (supportedApps[appName]) {
    return supportedApps[appName];
  }
  
  // Handle common variations and aliases
  if (appName.includes('whats') || appName.includes('wa')) {
    return supportedApps.whatsapp;
  }
  
  if (appName.includes('face') || appName.includes('fb')) {
    return supportedApps.facebook;
  }
  
  if (appName.includes('insta') || appName.includes('ig')) {
    return supportedApps.instagram;
  }
  
  if (appName.includes('twit') || appName === 'tw') {
    return supportedApps.twitter;
  }
  
  if (appName === 'x') {
    return supportedApps.x;
  }
  
  if (appName.includes('mail') || appName.includes('email')) {
    return supportedApps.gmail;
  }
  
  if (appName.includes('tube') || appName === 'yt') {
    return supportedApps.youtube;
  }
  
  if (appName.includes('google') || appName.includes('search')) {
    return supportedApps.google;
  }
  
  // Check for social media as a category
  if (appName.includes('social')) {
    // Return a generic error for social media since this should be handled by the voice control component
    return null;
  }
  
  // No match found
  return null;
};

// Send a message to a contact (currently simulated)
export const sendMessage = (contact, message) => {
  // Check for common messaging platforms by keyword in the contact name
  const lowerContact = contact.toLowerCase().trim();
  
  if (lowerContact.includes('whatsapp') || lowerContact.includes('wa')) {
    // If WhatsApp is explicitly mentioned, extract the phone number
    const phoneNumberMatch = lowerContact.match(/\d+/g);
    let phoneNumber = '';
    
    if (phoneNumberMatch && phoneNumberMatch.length > 0) {
      // Extract all digits as the phone number
      phoneNumber = phoneNumberMatch.join('');
    } else {
      // No number found in the command, extract the contact name
      const contactName = lowerContact.replace(/whatsapp|wa/gi, '').trim();
      if (contactName.length > 0) {
        // Just open WhatsApp web
        window.open(`https://web.whatsapp.com/`, '_blank');
        return {
          success: true, 
          message: `Opening WhatsApp Web. Please manually select contact "${contactName}" to send: "${message}"`
        };
      }
    }
    
    if (phoneNumber) {
      // Add India's country code (+91) if not present and it looks like an Indian number
      if (phoneNumber.length === 10 && !phoneNumber.startsWith('+') && !phoneNumber.startsWith('91')) {
        phoneNumber = `91${phoneNumber}`;
      }
      
      // Send through WhatsApp with the number
      return sendWhatsAppToNumber(phoneNumber, message);
    }
  } 
  else if (lowerContact.includes('gmail') || lowerContact.includes('email') || lowerContact.includes('mail')) {
    // Extract the email username without the platform mention
    const emailUsername = lowerContact.replace(/gmail|email|mail/gi, '').trim().replace(/\s+/g, '');
    
    // Check if it's just a username or complete email
    let emailAddress = '';
    if (emailUsername.includes('@')) {
      // It's already a complete email
      emailAddress = emailUsername;
    } else {
      // Add gmail.com domain if not present
      emailAddress = `${emailUsername}@gmail.com`;
    }
    
    // Open Gmail compose with pre-filled message
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${emailAddress}&body=${encodeURIComponent(message)}`, '_blank');
    return {
      success: true,
      message: `Email to ${emailAddress} drafted with message: "${message}".`
    };
  }
  else {
    // Default to WhatsApp as the most common messaging platform
    // Try to extract a phone number if any
    const phoneNumberMatch = lowerContact.match(/\d+/g);
    if (phoneNumberMatch && phoneNumberMatch.length > 0) {
      // Extract all digits as the phone number
      const phoneNumber = phoneNumberMatch.join('');
      // Add India's country code (+91) if not present
      if (phoneNumber.length === 10 && !phoneNumber.startsWith('+') && !phoneNumber.startsWith('91')) {
        return sendWhatsAppToNumber(`91${phoneNumber}`, message);
      } else {
        return sendWhatsAppToNumber(phoneNumber, message);
      }
    } else {
      // No number found, just open WhatsApp web
      window.open(`https://web.whatsapp.com/`, '_blank');
      return {
        success: true, 
        message: `Opening WhatsApp Web. Please manually select a contact to send: "${message}"`
      };
    }
  }
};

// New function to send WhatsApp message directly to a phone number
export const sendWhatsAppToNumber = (phoneNumber, message) => {
  try {
    // Clean the phone number to remove any non-digit characters except plus sign
    let cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    
    // If this is a 10-digit Indian number without country code, add +91
    if (/^\d{10}$/.test(cleanNumber)) {
      cleanNumber = `91${cleanNumber}`;
    } else if (cleanNumber.startsWith('+')) {
      // If has + prefix, remove it for the URL (WhatsApp Web expects numbers without +)
      cleanNumber = cleanNumber.substring(1);
    }
    
    // Make sure we have a number
    if (!cleanNumber || cleanNumber.length < 10) {
      return { success: false, message: "Invalid phone number format" };
    }

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Open WhatsApp Web with the number and message
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${cleanNumber}&text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    return { 
      success: true, 
      message: "WhatsApp opened. Please click Send in the WhatsApp window." 
    };
  } catch (error) {
    console.error("Error sending WhatsApp message:", error);
    return { 
      success: false, 
      message: "Failed to open WhatsApp. Please try again." 
    };
  }
};

// Send a WhatsApp message to a contact
export const sendWhatsAppMessage = (contact, message) => {
  try {
    console.log(`Sending WhatsApp message to: ${contact}`);
    
    // Clean up the contact name
    const cleanContact = contact.trim().toLowerCase();
    
    // Try to see if it's a phone number
    const phoneNumberMatch = cleanContact.match(/\d{10,}/);
    if (phoneNumberMatch) {
      // It's a phone number, use it directly
      return sendWhatsAppToNumber(cleanContact, message);
    }
    
    // Not a phone number, check if we have a number for this contact
    // or open WhatsApp Web directly
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://web.whatsapp.com`, '_blank');
    
    return { 
      success: true, 
      message: `WhatsApp opened. Please search for ${contact} and send: "${message}"` 
    };
  } catch (error) {
    console.error("Error opening WhatsApp:", error);
    return { 
      success: false, 
      message: "Failed to open WhatsApp. Please try again." 
    };
  }
};

// Logout function
export const logout = () => {
  // Clear local storage data related to authentication
  try {
    localStorage.removeItem('faceDescriptor');
    localStorage.removeItem('faceUsername');
    console.log('User data cleared from localStorage on logout');
  } catch (err) {
    console.error('Error clearing localStorage on logout:', err);
  }
  
  return {
    success: true,
    message: 'Logged out successfully'
  };
};

// In a real implementation, you might have additional functions for:
// - Retrieving contacts
// - Handling authentication with messaging services
// - Processing incoming messages
// - Managing connections to multiple messaging platforms 

export const onMessageSend = (contactInfo, message) => {
  try {
    console.log("Processing message to:", contactInfo, "Message:", message);
    
    const lowerContact = contactInfo.toLowerCase().trim();
    
    // Check if platform is specified
    if (lowerContact.includes('whatsapp') || lowerContact.includes('wa')) {
      // Extract phone number - look for patterns like digits
      const phoneNumberMatch = lowerContact.match(/\d{10,}/);
      
      if (phoneNumberMatch) {
        // We have a phone number in the contact info
        const phoneNumber = phoneNumberMatch[0];
        console.log("Extracted WhatsApp number:", phoneNumber);
        return sendWhatsAppToNumber(phoneNumber, message);
      } else {
        // No phone number found, open WhatsApp Web for manual selection
        console.log("No phone number found in:", lowerContact);
        return sendWhatsAppMessage(contactInfo.replace(/whatsapp|wa/gi, '').trim(), message);
      }
    } 
    else if (lowerContact.includes('email') || lowerContact.includes('gmail') || lowerContact.includes('mail')) {
      // Extract email username - process everything after the platform word
      let emailUser = lowerContact;
      
      // Remove platform words
      emailUser = emailUser.replace(/\bemail\b|\bgmail\b|\bmail\b/gi, '').trim();
      
      // Handle voice command spaces - Remove all spaces in the email portion
      emailUser = emailUser.replace(/\s+/g, '');
      
      // Check if it already has @ symbol
      if (!emailUser.includes('@')) {
        // Append gmail.com if no domain specified
        emailUser += '@gmail.com';
      }
      
      console.log("Sending email to:", emailUser);
      
      // Gmail URL - DO NOT use encodeURIComponent for the email address as it's causing issues with spaces
      // Instead, manually create a clean URL
      const emailParam = emailUser; // Already cleaned of spaces
      const bodyParam = encodeURIComponent(message);
      const subjectParam = encodeURIComponent("Message from Voice Assistant");
      
      const emailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${emailParam}&su=${subjectParam}&body=${bodyParam}`;
      console.log("Opening email URL:", emailUrl);
      window.open(emailUrl, '_blank');
      
      return { 
        success: true, 
        message: `Opening Gmail to send email to ${emailUser}`,
        emailAddress: emailUser // Return the cleaned email for display
      };
    }
    else {
      // No platform specified, default to WhatsApp using the contact info as a phone number
      // First check if it's a number
      const phoneNumberMatch = lowerContact.match(/\d{10,}/);
      
      if (phoneNumberMatch) {
        // It's a number, send directly to WhatsApp
        const phoneNumber = phoneNumberMatch[0];
        console.log("No platform specified, using number for WhatsApp:", phoneNumber);
        return sendWhatsAppToNumber(phoneNumber, message);
      } else {
        // Not a number, try sending as a contact name
        console.log("No platform or number found, using as contact name:", contactInfo);
        return sendWhatsAppMessage(contactInfo, message);
      }
    }
  } catch (error) {
    console.error("Error processing message:", error);
    return { 
      success: false, 
      message: "Failed to process your message. Please try again." 
    };
  }
}; 